import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Plus,
  Loader2,
  Building2,
  Hash,
  AtSign,
  AlertCircle,
  Sparkles,
  Search,
  ArrowLeft,
  Check,
  Trash2
} from 'lucide-react';
import { Brand, CreateBrandData } from '../../services/listeningService';
import listeningService from '../../services/listeningService';
import socialService from '../../services/socialService';
import { useProject } from '../../contexts/ProjectContext';
import Button from '../ui/Button';

interface BrandSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBrandData) => Promise<void>;
  onDelete?: (brandId: string) => Promise<void>;
  editingBrand?: Brand | null;
}

type ModalStep = 'choice' | 'form';

const BrandSetupModal: React.FC<BrandSetupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  editingBrand
}) => {
  const { t } = useTranslation();
  const { currentProject } = useProject();
  const [step, setStep] = useState<ModalStep>(editingBrand ? 'form' : 'choice');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [formData, setFormData] = useState<CreateBrandData>({
    name: editingBrand?.name || '',
    type: editingBrand?.type || 'own',
    keywords: editingBrand?.keywords || [],
    hashtags: editingBrand?.hashtags || [],
    handles: editingBrand?.handles || {},
    exclude_keywords: editingBrand?.exclude_keywords || []
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [hashtagInput, setHashtagInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');
  const [connectedAccounts, setConnectedAccounts] = useState<Record<string, string>>({});

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (editingBrand) {
        setStep('form');
        setFormData({
          name: editingBrand.name,
          type: editingBrand.type,
          keywords: editingBrand.keywords || [],
          hashtags: editingBrand.hashtags || [],
          handles: editingBrand.handles || {},
          exclude_keywords: editingBrand.exclude_keywords || []
        });
      } else {
        setStep('choice');
        setFormData({
          name: '',
          type: 'own',
          keywords: [],
          hashtags: [],
          handles: {},
          exclude_keywords: []
        });
      }
    }
  }, [isOpen, editingBrand]);

  // Fetch connected accounts for 'own' type brands
  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      try {
        const accountsResponse = await socialService.getAccounts();
        const accounts = accountsResponse.success ? accountsResponse.data : [];

        const handles: Record<string, string> = {};
        if (accounts && accounts.length > 0) {
          for (const account of accounts) {
            if (account.connected && account.username) {
              const platform = account.platform.replace('_reels', '').replace('_shorts', '');
              if (!handles[platform]) {
                handles[platform] = account.username.startsWith('@') ? account.username : `@${account.username}`;
              }
            }
          }
        }
        setConnectedAccounts(handles);
      } catch (error) {
        console.error('Error fetching connected accounts:', error);
      }
    };

    if (isOpen) {
      fetchConnectedAccounts();
    }
  }, [isOpen]);

  // Load business profile data
  const loadFromProfile = async () => {
    if (!currentProject?.id) return;

    setIsLoadingProfile(true);
    try {
      // Fetch both business profile and connected social accounts
      const [profileResponse, accountsResponse] = await Promise.all([
        listeningService.getBusinessProfile(currentProject.id),
        socialService.getAccounts()
      ]);

      const profile = profileResponse.success ? profileResponse.data : null;
      const accounts = accountsResponse.success ? accountsResponse.data : [];

      // Build form data from profile
      const newFormData: CreateBrandData = {
        name: profile?.business_name || currentProject.name || '',
        type: 'own',
        keywords: [],
        hashtags: [],
        handles: {},
        exclude_keywords: []
      };

      // Add business name as first keyword
      if (profile?.business_name) {
        newFormData.keywords?.push(profile.business_name);
      }

      // Add auto-suggested keywords
      if (profile?.auto_suggested_keywords && Array.isArray(profile.auto_suggested_keywords)) {
        const keywords = profile.auto_suggested_keywords.slice(0, 15);
        newFormData.keywords?.push(...keywords.filter((k: string) => !newFormData.keywords?.includes(k)));
      }

      // Add website analysis keywords
      if (profile?.website_analysis?.keywords && Array.isArray(profile.website_analysis.keywords)) {
        const keywords = profile.website_analysis.keywords.slice(0, 10);
        newFormData.keywords?.push(...keywords.filter((k: string) => !newFormData.keywords?.includes(k)));
      }

      // Add hashtags from content preferences
      if (profile?.content_preferences?.hashtag_groups && Array.isArray(profile.content_preferences.hashtag_groups)) {
        for (const group of profile.content_preferences.hashtag_groups) {
          if (group.hashtags && Array.isArray(group.hashtags)) {
            newFormData.hashtags?.push(...group.hashtags.filter((h: string) => !newFormData.hashtags?.includes(h)));
          }
        }
      }

      // Add social handles from connected accounts first (priority)
      const connectedHandles: Record<string, string> = {};
      if (accounts && accounts.length > 0) {
        for (const account of accounts) {
          if (account.connected && account.username) {
            const platform = account.platform.replace('_reels', '').replace('_shorts', '');
            if (!connectedHandles[platform]) {
              connectedHandles[platform] = account.username.startsWith('@') ? account.username : `@${account.username}`;
            }
          }
        }
      }

      // Merge with profile social_handles (connected accounts take priority)
      newFormData.handles = {
        instagram: connectedHandles.instagram || profile?.social_handles?.instagram || undefined,
        twitter: connectedHandles.twitter || profile?.social_handles?.twitter || undefined,
        facebook: connectedHandles.facebook || profile?.social_handles?.facebook || undefined,
        linkedin: connectedHandles.linkedin || profile?.social_handles?.linkedin || undefined,
        youtube: connectedHandles.youtube || profile?.social_handles?.youtube || undefined,
        tiktok: connectedHandles.tiktok || profile?.social_handles?.tiktok || undefined
      };

      setFormData(newFormData);
      setStep('form');
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback
      setFormData({
        name: currentProject?.name || '',
        type: 'own',
        keywords: currentProject?.name ? [currentProject.name] : [],
        hashtags: [],
        handles: {},
        exclude_keywords: []
      });
      setStep('form');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  // Start with empty form for other company
  const startOtherCompany = () => {
    setFormData({
      name: '',
      type: 'competitor',
      keywords: [],
      hashtags: [],
      handles: {},
      exclude_keywords: []
    });
    setStep('form');
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords?.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        keywords: [...(formData.keywords || []), keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData({
      ...formData,
      keywords: formData.keywords?.filter((k) => k !== keyword)
    });
  };

  const handleAddHashtag = () => {
    let tag = hashtagInput.trim();
    if (!tag.startsWith('#')) tag = '#' + tag;
    if (tag.length > 1 && !formData.hashtags?.includes(tag)) {
      setFormData({
        ...formData,
        hashtags: [...(formData.hashtags || []), tag]
      });
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (hashtag: string) => {
    setFormData({
      ...formData,
      hashtags: formData.hashtags?.filter((h) => h !== hashtag)
    });
  };

  const handleAddExclude = () => {
    if (excludeInput.trim() && !formData.exclude_keywords?.includes(excludeInput.trim())) {
      setFormData({
        ...formData,
        exclude_keywords: [...(formData.exclude_keywords || []), excludeInput.trim()]
      });
      setExcludeInput('');
    }
  };

  const handleRemoveExclude = (keyword: string) => {
    setFormData({
      ...formData,
      exclude_keywords: formData.exclude_keywords?.filter((k) => k !== keyword)
    });
  };

  const handleHandleChange = (platform: string, value: string) => {
    const newHandles = { ...formData.handles };
    if (value) {
      newHandles[platform] = value;
    } else {
      delete newHandles[platform];
    }
    setFormData({
      ...formData,
      handles: newHandles
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting brand:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const platforms = [
    { key: 'instagram', label: 'Instagram', placeholder: '@seuhandle' },
    { key: 'twitter', label: 'Twitter/X', placeholder: '@seuhandle' },
    { key: 'facebook', label: 'Facebook', placeholder: '/suapagina' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: '/company/suaempresa' },
    { key: 'youtube', label: 'YouTube', placeholder: '@seucanal' },
    { key: 'tiktok', label: 'TikTok', placeholder: '@seuhandle' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            {step === 'form' && !editingBrand && (
              <button
                onClick={() => setStep('choice')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mr-1"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingBrand ? t('listening.modal.editBrand', 'Edit Brand') : step === 'choice' ? t('listening.modal.newBrand', 'New Brand to Monitor') : t('listening.modal.configureBrand', 'Configure Brand')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {step === 'choice'
                  ? t('listening.modal.chooseType', 'Choose monitoring type')
                  : t('listening.modal.configureKeywords', 'Configure keywords and profiles to monitor')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {step === 'choice' ? (
          <div className="p-6 space-y-4">
            {/* Option 1: Use my data */}
            <button
              onClick={loadFromProfile}
              disabled={isLoadingProfile}
              className="w-full p-6 bg-gradient-to-br from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 border-2 border-primary-200 dark:border-primary-700 rounded-xl hover:border-primary-400 dark:hover:border-primary-500 transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center flex-shrink-0">
                  {isLoadingProfile ? (
                    <Loader2 className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" />
                  ) : (
                    <Sparkles className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('listening.modal.useMyData', 'Use my data')}
                    </h3>
                    <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-400 text-xs font-medium rounded-full">
                      {t('listening.modal.recommended', 'Recommended')}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {t('listening.modal.autoFillDescription', 'Auto-fill with your business profile information.')}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      <Check className="w-3 h-3 text-green-500" /> {t('listening.modal.businessName', 'Business name')}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      <Check className="w-3 h-3 text-green-500" /> {t('listening.modal.websiteKeywords', 'Website keywords')}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      <Check className="w-3 h-3 text-green-500" /> {t('listening.modal.socialNetworks', 'Social networks')}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      <Check className="w-3 h-3 text-green-500" /> {t('listening.modal.hashtags', 'Hashtags')}
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Option 2: Search other company */}
            <button
              onClick={startOtherCompany}
              className="w-full p-6 bg-gray-50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-gray-400 dark:hover:border-gray-500 transition-all text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {t('listening.modal.monitorOther', 'Monitor another company')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {t('listening.modal.monitorOtherDesc', 'Track what people are saying about competitors, partners, or any other brand.')}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      {t('listening.modal.competitors', 'Competitors')}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      {t('listening.modal.partners', 'Partners')}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
                      {t('listening.modal.influencers', 'Influencers')}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Brand Name & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('listening.modal.brandName', 'Brand name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('listening.modal.brandNamePlaceholder', 'Ex: My Company')}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('listening.modal.type', 'Type')}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'own' | 'competitor' })}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="own">{t('listening.myBrand', 'My brand')}</option>
                  <option value="competitor">{t('listening.competitor', 'Competitor')}</option>
                </select>
              </div>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('listening.modal.keywordsToMonitor', 'Keywords to monitor')}
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                  placeholder={t('listening.modal.keywordPlaceholder', 'Ex: brand name, product')}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button type="button" variant="secondary" onClick={handleAddKeyword}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.keywords?.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="hover:text-blue-900 dark:hover:text-blue-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Hashtags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Hash className="w-4 h-4 inline mr-1" />
                Hashtags
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHashtag())}
                  placeholder={t('listening.modal.hashtagPlaceholder', 'Ex: #yourbrand')}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button type="button" variant="secondary" onClick={handleAddHashtag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.hashtags?.map((hashtag) => (
                  <span
                    key={hashtag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-lg text-sm"
                  >
                    {hashtag}
                    <button
                      type="button"
                      onClick={() => handleRemoveHashtag(hashtag)}
                      className="hover:text-purple-900 dark:hover:text-purple-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Social Handles - Only for 'own' type brands */}
            {formData.type === 'own' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <AtSign className="w-4 h-4 inline mr-1" />
                  {t('listening.modal.socialProfiles', 'Social media profiles')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {t('listening.modal.socialProfilesInfo', 'Connected accounts from your social media integrations.')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {platforms.map((platform) => (
                    <div key={platform.key} className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400 w-20">
                        {platform.label}
                      </span>
                      <input
                        type="text"
                        value={connectedAccounts[platform.key] || formData.handles?.[platform.key] || ''}
                        readOnly
                        disabled
                        placeholder={t('listening.modal.notConnected', 'Not connected')}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 placeholder-gray-400 cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      {t('listening.modal.competitorInfo', 'Competitor monitoring')}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      {t('listening.modal.competitorInfoDesc', 'For competitors, mentions are monitored via Twitter/X public search only. Instagram, Facebook, and LinkedIn require direct account access.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Exclude Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                {t('listening.modal.excludeKeywords', 'Words to ignore')}
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={excludeInput}
                  onChange={(e) => setExcludeInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExclude())}
                  placeholder={t('listening.modal.excludePlaceholder', 'Ex: word to ignore')}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <Button type="button" variant="secondary" onClick={handleAddExclude}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.exclude_keywords?.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 rounded-lg text-sm"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => handleRemoveExclude(keyword)}
                      className="hover:text-gray-900 dark:hover:text-gray-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                {editingBrand && onDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={async () => {
                      if (confirm(`${t('listening.modal.confirmDeleteBrand', 'Are you sure you want to delete')} "${editingBrand.name}"?`)) {
                        await onDelete(editingBrand.id);
                        onClose();
                      }
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('listening.modal.deleteBrand', 'Delete brand')}
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!formData.name.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t('listening.modal.saving', 'Saving...')}
                    </>
                  ) : editingBrand ? (
                    t('listening.modal.saveChanges', 'Save changes')
                  ) : (
                    t('listening.modal.startMonitoring', 'Start monitoring')
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BrandSetupModal;
